import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const LABEL_CLASSES = {
  blue: 'bg-blue-700 text-white',
  pink: 'text-black bg-pink-400',
  gold: 'text-black bg-gold-400',
  lightBlue: 'text-black bg-blue-400',
  grey: 'text-black bg-grey-500',
  green: 'text-white bg-green-400',
};

function PropertyCard(props) {
  const labelClass = LABEL_CLASSES[props.label_colour] || LABEL_CLASSES.blue;
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <>
      <div className="relative">
        <div className="h-full w-full text-white">
          {props.sticker && (
            <div>
              <img
                src={props.sticker.url}
                alt={props.sticker.alt}
                className="absolute top-5 right-5 z-10 w-23"
              />
            </div>
          )}

          {props.label_text && (
            <div className="absolute bottom-5 left-5 z-2">
              <div
                className={`font-medium rounded-[2px] text-xs uppercase pt-1.25 pb-1 px-2 text-center !tracking-wider ${labelClass}`}
              >
                {props.label_text}
              </div>
            </div>
          )}

          <div>
            <div className="max-h-104">
              <picture>
                <AnimatePresence>
                  {!imgLoaded && (
                    <motion.div
                      key="img-fade-placeholder"
                      initial={{ opacity: 1 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: '#f3f4f6',
                      }}
                    />
                  )}
                  {
                    <motion.img
                      key="img-fade"
                      className="lg:w-full lg:h-full aspect-[181/105] object-cover object-center"
                      src={props.thumbnail}
                      srcSet={`${props.thumbnail} 1x, ${
                        props.thumbnail_retina || props.thumbnail
                      } 2x`}
                      sizes="(min-width: 1220px) 488px, 100vw"
                      width="488"
                      height="283"
                      alt={props?.thumbnail_meta?.alt}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: imgLoaded ? 1 : 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      onLoad={() => setImgLoaded(true)}
                    />
                  }
                </AnimatePresence>
              </picture>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white px-8 py-5 md:px-5">
        <h3 className="text-2xl font-light text-black">{props.name}</h3>

        {props?.development?.name && (
          <p className="text-sm font-medium mb-6">
            {props.development.name} - {props.location.city}
            {props.location.post_code && `, ${props.location.post_code}`}
          </p>
        )}

        {props.plots && (
          <div className="text-sm lg:text-base text-black font-light mb-2 lg:mb-6">
            {props.plots} {props.plots === 1 ? 'plot' : 'plots'} available
          </div>
        )}

        <div className="flex flex-col gap-2 mb-5">
          {props.bedrooms && (
            <div className="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="14"
                fill="none"
                className="mr-3 text-blue-700 min-w-[22px]"
                viewBox="0 0 22 14"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M4.3 3.9c-.2 0-.4.2-.4.4v2h4.7v-2c0-.2-.1-.4-.3-.4h-4ZM2 13.6c0 .2.1.4.4.4h3.1c.2 0 .4-.2.4-.4v-1.2h-4v1.2Z"
                />
                <path
                  fill="currentColor"
                  d="M3.1 4.3c0-.7.6-1.2 1.2-1.2h4c.6 0 1.1.5 1.1 1.2v2h3.2v-2c0-.7.5-1.2 1.1-1.2h4c.6 0 1.2.5 1.2 1.2v2h1.5V1.1C20.4.5 20 0 19.2 0H2.7c-.6 0-1.1.5-1.1 1.2v5H3v-2Z"
                />
                <path
                  fill="currentColor"
                  d="M13.7 3.9c-.2 0-.3.2-.3.4v2H18v-2c0-.2-.2-.4-.4-.4h-4Zm2.4 9.7c0 .2.2.4.4.4h3.1c.3 0 .4-.2.4-.4v-1.2h-3.9v1.2ZM20.8 7H1.2C.5 7 0 7.5 0 8.2v3c0 .3.2.5.4.5h21.2c.2 0 .4-.2.4-.4V8.2c0-.7-.5-1.2-1.2-1.2Z"
                />
              </svg>

              <div className="text-black text-sm font-light">
                {Array.isArray(props.bedrooms)
                  ? props.bedrooms
                      .map((option, idx) => (
                        <span key={idx}>
                          {idx > 0
                            ? idx === props.bedrooms.length - 1
                              ? ' & '
                              : ', '
                            : ''}
                          {option.number_of_bedrooms}
                        </span>
                      ))
                      .concat(' bedrooms')
                  : props.bedrooms}
              </div>
            </div>
          )}

          {props.bathrooms && (
            <div className="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="10"
                viewBox="0 0 15 10"
                className="mr-3 text-blue-700 min-w-[22px]"
                fill="none"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M14.6429 3.77156H4.29571C4.26902 3.76503 4.24171 3.76156 4.21429 3.76121L2.14286 3.77008L2.15286 1.87691C2.16571 1.23944 2.54 0.739522 3.01 0.739522C3.52442 0.744789 3.95961 1.13466 4.03857 1.66097C4.06145 1.86518 4.2399 2.01152 4.43714 1.98783C4.63439 1.96415 4.77574 1.77939 4.75286 1.57518C4.63387 0.674694 3.89214 0.00267769 3.01429 0C2.15714 0 1.46571 0.817911 1.44286 1.86803L1.42857 3.77156H0.357143C0.159898 3.77156 0 3.93711 0 4.14132V4.75956C0.00191809 6.45859 1.15048 7.92514 2.75571 8.2782L2.05286 9.41411C1.97564 9.52509 1.9639 9.67113 2.02234 9.7938C2.08077 9.91647 2.19984 9.99572 2.33189 9.99983C2.46394 10.0039 2.5874 9.93226 2.65286 9.81345L3.55714 8.35659H11.4429L12.3471 9.81345C12.4127 9.91926 12.5257 9.98333 12.6471 9.98354C12.7776 9.98332 12.8976 9.90944 12.9601 9.79084C13.0226 9.67225 13.0176 9.52781 12.9471 9.41411L12.2443 8.2782C13.8495 7.92514 14.9981 6.45859 15 4.75956V4.14132C15 3.93711 14.8401 3.77156 14.6429 3.77156Z"
                  fill="currentColor"
                />
              </svg>

              <div className="text-black text-sm font-light">
                {props.bathrooms}
              </div>
            </div>
          )}
        </div>

        {(() => {
          if (props.min_price > 0) {
            const formattedPrice =
              typeof props.min_price === 'string'
                ? parseInt(props.min_price.replace(/,/g, '')).toLocaleString()
                : props.min_price.toLocaleString();

            return (
              <div className="mb-5 text-black">
                From{' '}
                <span className="font-light">
                  <strong className="font-medium">
                    &pound;{formattedPrice}
                  </strong>
                </span>
              </div>
            );
          } else {
            return (
              <div className="mb-6 text-lg text-black">
                {props.status?.label}
              </div>
            );
          }
        })()}

        <div className="flex flex-col lg:flex-row lg:items-center">
          <a
            href={props.url}
            className="btn btn--blue btn--sml mr-0 mb-5 lg:mr-5 lg:mb-0"
          >
            <svg className="w-[7px] h-[10px]">
              <use xlinkHref="#icon-chevron" />
            </svg>
            <span>
              Discover <span className="sr-only">{props.name}</span>
            </span>
          </a>

          {/* <button
            className="js-overlay-open cursor-pointer btn btn--grey-light btn--sml"
            data-overlay="enquiry"
            data-prop-name={props.name}
            data-development={props.coins_id}
          >
            <svg className="w-[7px] h-[10px]">
              <use xlinkHref="#icon-plus" />
            </svg>
            <span>Enquire now</span>
          </button> */}
        </div>
      </div>
    </>
  );
}

export default React.memo(
  PropertyCard,
  (prevProps, nextProps) => prevProps.id === nextProps.id
);
